import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  useDraftSitrepsV2,
  useSitrepsPendingReview,
  useSitrepsV2,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import {
  applyReportsFilterToSearchParams,
  customReportLabel,
  useReportsFilterOptions,
  type ReportsFilterSelection,
} from 'components/Reports/hooks/useReportsFilterOptions';
import { useScopedSitrepViewData } from 'components/Reports/hooks/useScopedSitrepViewData';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { useResourceSets } from 'components/IAP/hooks/resourceSetHooks';
import CreateCustomReportDialog from 'components/Reports/CreateCustomReportDialog';
import ReportsFilterBar from 'components/Reports/ReportsFilterBar';
import { registerReportsTourApi } from 'components/Reports/reportsTour/reportsTourBridge';
import { REPORTS_TOUR_PARAM } from 'components/Reports/reportsTour/reportsTourSteps';
import { findAorByHomeId } from 'components/MapPage/CorporateViewTabs/AorsTab/utils/findAorByHomeId';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import { useAors } from 'hooks/useAors';
import { useAorText } from 'utils/aorTerminology';
import { useReportVersioningFlag } from 'utils/featureFlags';
import 'styles/pages/_reports.scss';
import { Spinner } from '@/components/ui/spinner';
import { ReportsEmptyState } from 'components/Reports/ReportsEmptyState';
import ReportsSitrepView from 'components/Reports/ReportsSitrepView';
import { SitrepViewTabs } from 'components/Reports/reports.types';
import {
  selectGroupGuid,
  selectIncident,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';
import { useLocation, useSearchParams } from 'react-router';

/** Last name from A to Z. Equal names break the tie by id. */
const lastByName = <T extends { id: string; name: string }>(items: T[]) =>
  [...items]
    .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id))
    .at(-1);

const ReportsView: React.FC = () => {
  const aorText = useAorText();
  const [searchParams, setSearchParams] = useSearchParams();
  const incidentId = searchParams.get('incidentId');
  const aorId = searchParams.get('aorId');
  const assetId = searchParams.get('assetId');
  const customReportId = searchParams.get('customReportId');
  const [showCreateCustomReport, setShowCreateCustomReport] = useState(false);

  const {
    data: reports = [],
    isLoading: isLoadingReports,
    isError: isErrorReports,
    refetch: refetchReports,
  } = useSitrepsV2();
  const {
    data: draftSitreps = [],
    isLoading: isLoadingDrafts,
    isError: isErrorDrafts,
    refetch: refetchDrafts,
  } = useDraftSitrepsV2();
  const {
    data: sitrepsPendingReview = [],
    isLoading: isLoadingPendingReviews,
    isError: isErrorPendingReviews,
    refetch: refetchPendingReviews,
  } = useSitrepsPendingReview();

  // Get AORs and Incidents for filter name
  const { data: aors = [], isFetched: isAorsFetched } = useAors();
  const { data: incidents = [], isFetched: isIncidentsFetched } =
    useAllIncidents();
  const { data: resourceSets = [] } = useResourceSets();
  const assets = useMemo(
    () => resourceSets.filter((resourceSet) => !resourceSet.archived),
    [resourceSets]
  );
  const customReports = useMemo(
    () =>
      [...reports, ...draftSitreps, ...sitrepsPendingReview].filter(
        (sitrep) => sitrep.subject_kind === 'custom'
      ),
    [draftSitreps, reports, sitrepsPendingReview]
  );
  const currentIncident = useAppSelector(selectIncident);
  const groupGuid = useAppSelector(selectGroupGuid);
  const userGuid = useAppSelector(selectUserGuid);
  const { data: roster, isFetched: isRosterFetched } = useGetRostersForGroup(
    groupGuid ?? ''
  );
  const location = useLocation();
  const hasAppliedHomeAorDefaultRef = useRef(false);

  const isInIAPContext = useMemo(() => {
    return location.pathname.includes('/iap/') && !!currentIncident;
  }, [currentIncident, location.pathname]);

  useEffect(() => {
    if (!isInIAPContext || !currentIncident) {
      return;
    }

    if (
      incidentId === currentIncident.id &&
      !aorId &&
      !assetId &&
      !customReportId
    ) {
      return;
    }

    setSearchParams(
      (currentSearchParams) => {
        currentSearchParams.set('incidentId', currentIncident.id);
        currentSearchParams.delete('aorId');
        currentSearchParams.delete('assetId');
        currentSearchParams.delete('customReportId');
        return currentSearchParams;
      },
      { replace: true }
    );
  }, [
    aorId,
    assetId,
    currentIncident,
    customReportId,
    incidentId,
    isInIAPContext,
    setSearchParams,
  ]);

  useEffect(() => {
    if (isInIAPContext || hasAppliedHomeAorDefaultRef.current) {
      return;
    }

    if (!isAorsFetched || !isIncidentsFetched) {
      return;
    }

    const aorIsValid = !!aorId && aors.some((aor) => aor.id === aorId);
    const incidentIsValid =
      !!incidentId && incidents.some((incident) => incident.id === incidentId);
    if (aorIsValid || incidentIsValid || assetId || customReportId) {
      hasAppliedHomeAorDefaultRef.current = true;
      return;
    }

    const currentUserRoster = isRosterFetched
      ? roster?.find((r) => r.user_guid === userGuid)
      : undefined;
    const homeAor = findAorByHomeId(aors, currentUserRoster?.home_aor);
    const chosenAor = homeAor ?? lastByName(aors);
    if (!chosenAor && !isRosterFetched && incidents.length === 0) return;
    if (chosenAor) {
      hasAppliedHomeAorDefaultRef.current = true;
      setSearchParams(
        (currentSearchParams) => {
          currentSearchParams.set('aorId', chosenAor.id);
          currentSearchParams.delete('incidentId');
          return currentSearchParams;
        },
        { replace: true }
      );
      return;
    }

    hasAppliedHomeAorDefaultRef.current = true;
    const chosenIncident = lastByName(incidents);
    if (!chosenIncident) return;

    setSearchParams(
      (currentSearchParams) => {
        currentSearchParams.set('incidentId', chosenIncident.id);
        currentSearchParams.delete('aorId');
        return currentSearchParams;
      },
      { replace: true }
    );
  }, [
    aorId,
    aors,
    assetId,
    customReportId,
    incidentId,
    incidents,
    isAorsFetched,
    isIncidentsFetched,
    isInIAPContext,
    isRosterFetched,
    roster,
    setSearchParams,
    userGuid,
  ]);

  const selectedIncident = useMemo(() => {
    const effectiveIncidentId =
      incidentId ?? (isInIAPContext ? currentIncident?.id : null);

    if (!effectiveIncidentId) {
      return null;
    }

    return incidents.find((i) => i.id === effectiveIncidentId) || null;
  }, [currentIncident?.id, incidentId, incidents, isInIAPContext]);

  const selectedAorId = useMemo(() => {
    return aorId ? (aorId as UUID) : null;
  }, [aorId]);

  const selectedAsset = useMemo(
    () =>
      assetId
        ? (assets.find((asset) => asset.resource_set_id === assetId) ?? null)
        : null,
    [assetId, assets]
  );

  const selectedCustomReport = useMemo(
    () =>
      customReportId
        ? (customReports.find((report) => report.id === customReportId) ?? null)
        : null,
    [customReportId, customReports]
  );

  const selectedFilter = useMemo<ReportsFilterSelection | null>(() => {
    if (customReportId && !isInIAPContext) {
      return {
        value: customReportId as UUID,
        label: selectedCustomReport
          ? customReportLabel(selectedCustomReport)
          : 'Custom area',
        kind: 'custom',
      };
    }

    if (assetId && !isInIAPContext) {
      return {
        value: assetId as UUID,
        label: selectedAsset?.name ?? 'Asset',
        kind: 'asset',
      };
    }

    if (selectedIncident) {
      return {
        value: selectedIncident.id,
        label: selectedIncident.name,
        kind: 'incident',
      };
    }

    if (selectedAorId) {
      const selectedAor = aors.find((aor) => aor.id === selectedAorId);

      if (selectedAor) {
        return {
          value: selectedAor.id,
          label: selectedAor.name,
          kind: 'aor',
        };
      }
    }

    return null;
  }, [
    aors,
    assetId,
    customReportId,
    isInIAPContext,
    selectedAorId,
    selectedAsset,
    selectedCustomReport,
    selectedIncident,
  ]);

  const { groupedOptions, selectedOption } = useReportsFilterOptions({
    aors,
    incidents,
    assets: isInIAPContext ? [] : assets,
    customReports: isInIAPContext ? [] : customReports,
    selectedFilter,
  });

  const wantsTour = searchParams.get(REPORTS_TOUR_PARAM) === '1';
  const tourStartedRef = useRef(false);
  const selectedFilterRef = useRef(selectedFilter);
  selectedFilterRef.current = selectedFilter;
  const groupedOptionsRef = useRef(groupedOptions);
  groupedOptionsRef.current = groupedOptions;
  const handleSelectedFilterChangeRef = useRef<
    (next: ReportsFilterSelection) => void
  >(() => {});
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;
  const reportVersioning = useReportVersioningFlag();
  const reportVersioningRef = useRef(reportVersioning);
  reportVersioningRef.current = reportVersioning;

  const handleSelectedFilterChange = (
    nextSelectedFilter: ReportsFilterSelection
  ) => {
    setSearchParams(
      (nextSearchParams) =>
        applyReportsFilterToSearchParams(nextSearchParams, nextSelectedFilter),
      { replace: true }
    );
  };

  handleSelectedFilterChangeRef.current = handleSelectedFilterChange;

  useEffect(() => {
    return registerReportsTourApi({
      ensureScope: async () => {
        if (selectedFilterRef.current) return;
        const deadline = Date.now() + 8000;
        while (!selectedFilterRef.current && Date.now() < deadline) {
          const options = groupedOptionsRef.current.flatMap(
            (group) => group.options
          );
          const aorOption = options.find((option) => option.kind === 'aor');
          const choice = aorOption ?? options.find(Boolean);
          if (choice) {
            handleSelectedFilterChangeRef.current(choice);
            return;
          }
          await new Promise((resolve) => window.setTimeout(resolve, 100));
        }
      },
      ensureCurrentTab: () => {
        setSearchParamsRef.current(
          (params) => {
            params.set('sitrepTab', SitrepViewTabs.CurrentSitrep);
            return params;
          },
          { replace: true }
        );
      },
      isReportVersioning: () => reportVersioningRef.current,
    });
  }, []);

  useEffect(() => {
    if (!wantsTour || tourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      if (tourStartedRef.current) return;
      tourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(REPORTS_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('components/Reports/reportsTour/reportsTour')
        .then(({ startReportsTour }) => {
          startReportsTour();
        })
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [wantsTour, setSearchParams]);

  // Calculate the filter name based on current selection
  const filterName = selectedFilter?.label ?? null;

  const hasSelectedFilter = Boolean(selectedFilter);
  const scopeListsEmpty =
    isAorsFetched &&
    isIncidentsFetched &&
    aors.length === 0 &&
    incidents.length === 0;

  const selectedKind = selectedFilter?.kind ?? null;
  const selectedScope = useMemo(
    () => ({
      incidentId: selectedIncident?.id ?? null,
      aorId: selectedAorId,
      assetId: selectedKind === 'asset' ? (assetId as UUID) : null,
      customReportId:
        selectedKind === 'custom' ? (customReportId as UUID) : null,
    }),
    [assetId, customReportId, selectedAorId, selectedIncident?.id, selectedKind]
  );

  const reportAsset = useMemo(() => {
    if (selectedKind !== 'asset' || !assetId) return undefined;
    const ownAorId = [
      selectedAsset?.aor,
      selectedAsset?.opcon,
      selectedAsset?.tacon,
    ].find((id): id is string => typeof id === 'string' && id.length > 0);
    return {
      id: assetId,
      aorId: ownAorId ?? selectedAorId ?? undefined,
    };
  }, [assetId, selectedAorId, selectedAsset, selectedKind]);

  const openCustomReport = (sitrepId: string) => {
    setSearchParams(
      (params) =>
        applyReportsFilterToSearchParams(params, {
          kind: 'custom',
          value: sitrepId as UUID,
        }),
      { replace: true }
    );
  };

  const sitrepViewData = useMemo(
    () => ({
      publishedSitreps: {
        data: reports,
        isLoading: isLoadingReports,
        isError: isErrorReports,
        refetch: refetchReports,
      },
      draftSitreps: {
        data: draftSitreps,
        isLoading: isLoadingDrafts,
        isError: isErrorDrafts,
        refetch: refetchDrafts,
      },
      pendingReviews: {
        data: sitrepsPendingReview,
        isLoading: isLoadingPendingReviews,
        isError: isErrorPendingReviews,
        refetch: refetchPendingReviews,
      },
    }),
    [
      draftSitreps,
      isErrorDrafts,
      isErrorPendingReviews,
      isErrorReports,
      isLoadingDrafts,
      isLoadingPendingReviews,
      isLoadingReports,
      refetchDrafts,
      refetchPendingReviews,
      refetchReports,
      reports,
      sitrepsPendingReview,
    ]
  );

  const scopedSitrepViewData = useScopedSitrepViewData({
    ...sitrepViewData,
    scope: selectedScope,
  });

  if (isErrorReports) {
    return (
      <div className="reports-view dark tw:flex tw:h-full tw:min-h-0 tw:flex-1 tw:flex-col tw:overflow-hidden">
        <ReportsEmptyState
          icon="error"
          title="Error loading reports"
          description={aorText(
            'Try refreshing the page or selecting a different AOR or incident.'
          )}
        />
      </div>
    );
  }

  return (
    <div
      className="reports-view dark tw:flex tw:h-full tw:min-h-0 tw:flex-1 tw:flex-col tw:overflow-hidden"
      data-tour="reports-intro"
    >
      <div className="reports-view__top-controls tw:shrink-0 tw:bg-background">
        <div className="reports-view__filter-bar" data-tour="reports-filter">
          <ReportsFilterBar
            groupedOptions={groupedOptions}
            selectedFilter={selectedOption}
            onSelectedFilterChange={handleSelectedFilterChange}
            onCreateCustomReport={
              isInIAPContext ? undefined : () => setShowCreateCustomReport(true)
            }
            disableFilter={isInIAPContext} // Disable filter if we're already in an incident context
          />
        </div>
      </div>
      <CreateCustomReportDialog
        show={showCreateCustomReport}
        onHide={() => setShowCreateCustomReport(false)}
        fallbackAorId={selectedAorId}
        onCreated={(sitrepId) => {
          setShowCreateCustomReport(false);
          openCustomReport(sitrepId);
        }}
      />

      {!hasSelectedFilter ? (
        scopeListsEmpty ? (
          <ReportsEmptyState
            icon="filter"
            title={aorText('Select an AOR or incident')}
            description={aorText(
              'Please select an AOR or Incident to view reports.'
            )}
          />
        ) : (
          <Spinner className="tw:size-6" />
        )
      ) : (
        <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col">
          <ReportsSitrepView
            scopeName={filterName || 'your organization'}
            publishedSitreps={scopedSitrepViewData.publishedSitreps}
            draftSitreps={scopedSitrepViewData.draftSitreps}
            pendingReviews={scopedSitrepViewData.pendingReviews}
            aors={aors}
            aorId={
              selectedKind === 'aor'
                ? selectedAorId || undefined
                : selectedKind === 'custom'
                  ? selectedCustomReport?.aor_id || undefined
                  : undefined
            }
            incident={
              selectedKind === 'incident'
                ? selectedIncident || undefined
                : undefined
            }
            asset={reportAsset}
            isCustomArea={selectedKind === 'custom'}
          />
        </div>
      )}
    </div>
  );
};

export default ReportsView;
